/**
 * Store-layer types for agent chat state.
 */

import type {ContentPart} from '@wandb/forge-shell-contract/aria';

import type {
  AgentQuestion,
  NetworkAccessRequest,
} from '../../client/WbAgentClient';

export type MessageRole = 'user' | 'assistant' | 'system';

export type RunOutcome = 'finished' | 'errored' | 'cancelled';

/**
 * ActiveThreadLoadFailure discriminates how the active thread's load failed.
 * 'not-found' means the load returned no turns; 'error' carries a message.
 */
export type ActiveThreadLoadFailure =
  | {kind: 'error'; message: string}
  | {kind: 'not-found'};

/**
 * ThreadLoadState is a thread's transient load-attempt lifecycle, keyed by
 * thread id in `AgentChatState.threadLoads`. Absent means the thread is not
 * being loaded (never attempted, or already `loaded`). Distinct from
 * `Thread.loadStatus`, which is the persistent 'summary' | 'loaded' identity.
 */
export type ThreadLoadState =
  | {status: 'loading'}
  | {status: 'failed'; failure: ActiveThreadLoadFailure};

/**
 * INVARIANT — thread identity rotation:
 *
 * A new chat begins as a `drafts[<client-uuid>]` entry. On the first
 * `turn-created` event, the thread is promoted into `threads[<threadId>]`,
 * a `promotionAliases[<client-uuid>] = <threadId>` entry is recorded, and
 * `thread.id` is rewritten to `<threadId>`.
 *
 * `activeThreadId` is the one identifier that does NOT rotate. It stays as
 * the creation-time client UUID for the lifetime of the session, because AUI
 * remounts the entire thread runtime on any `threadList.threadId` change
 * (verified against @assistant-ui/core v0.12.17), and remounting mid-stream
 * loses the in-flight assistant response.
 *
 * Consumers must therefore:
 *   - resolve thread reads via `findThread`, which transparently follows
 *     `promotionAliases`,
 *   - compare against the active thread via `isActiveThread`, NOT raw
 *     `=== state.activeThreadId`,
 *   - never key long-lived external state (pollers, AbortControllers,
 *     subscriptions, etc.) by `activeThreadId`; use the canonical
 *     `thread.id` or resolve through `findThread`.
 */
export interface AgentChatState {
  threads: Record<string, Thread>;
  drafts: Record<string, Thread>;
  promotionAliases: Record<string, string>;
  activeThreadId: string | null;
  /**
   * threadLoads holds the transient load state of threads being fetched or that
   * failed to fetch, keyed by thread id. The active thread's spinner/failure is
   * derived from its entry (see `selectActiveThreadLoadState`); a `loaded`
   * thread has no entry.
   */
  threadLoads: Record<string, ThreadLoadState>;
  /**
   * pendingRenames is the in-flight rename overlay; entries override
   * `Thread.title` for display.
   */
  pendingRenames: Record<string, {title: string}>;
  /**
   * pendingDeletes holds ids of threads currently being deleted; entries
   * hide the row from the chat history until the server confirms or the
   * mutation rolls back.
   */
  pendingDeletes: Set<string>;
  /**
   * deletedThreadIds holds ids whose server-side delete has been confirmed.
   * Sticky for the session so a late list refetch can't re-add a row.
   */
  deletedThreadIds: Set<string>;
}

type ThreadBuckets<T> = {
  threads: Record<string, T>;
  drafts: Record<string, T>;
  promotionAliases: Record<string, string>;
};

export function findThread<T>(
  state: ThreadBuckets<T>,
  id: string
): T | undefined {
  return (
    state.threads[id] ??
    state.drafts[id] ??
    state.threads[state.promotionAliases[id] ?? '']
  );
}

/**
 * isActiveThread tells whether `id` refers to the same thread as the store's
 * sticky `activeThreadId`, after resolving promotion aliases on both sides.
 *
 * Required because `activeThreadId` does not rotate on promotion (see the
 * invariant comment on `AgentChatState`): a raw `id === state.activeThreadId`
 * check returns false when one side is the original client UUID and the other
 * is the post-promotion server thread id, even though both name the same thread.
 *
 * Note: when `id` no longer resolves to a known thread (e.g. mid-deletion,
 * after the alias chain has been wiped), this returns false — callers that
 * care about the "was this active before the deletion?" state must capture
 * the result before mutating the buckets.
 */
export function isActiveThread<T>(
  state: ThreadBuckets<T> & {activeThreadId: string | null},
  id: string
): boolean {
  const active = state.activeThreadId;
  if (active == null) {
    return false;
  }
  if (active === id) {
    return true;
  }
  const target = findThread(state, id);
  return target !== undefined && findThread(state, active) === target;
}

export interface Thread {
  isPinned: boolean;
  id: string;
  title: string | null;
  /**
   * loadStatus defines whether the thread is a 'summary' (metadata-only) or
   * fully-hydrated with a message tree.
   */
  loadStatus: 'summary' | 'loaded';
  status: 'idle' | 'running' | 'error';
  error: string | null;
  /**
   * Newest backend turn known for this thread. Used as a parent fallback when
   * the active frontend node is local/optimistic and does not encode a backend
   * turn id yet. This is backend turn identity, not frontend run identity.
   */
  latestTurnId: string | null;
  messageTree: MessageTree;
  currentRunId: string | null;
  currentMessageId: string | null;
  /**
   * cancelRequested marks a user stop request for the in-flight run, layered
   * over `status`. The derived `cancelling` view is
   * `status === 'running' && cancelRequested`; it is cleared when the run
   * settles or a new run starts.
   */
  cancelRequested: boolean;
  runOutcomes: Record<string, RunOutcome>;
  /**
   * turnOutcomes records each settled turn's terminal outcome keyed by turnId,
   * so a turn can render its outcome (e.g. a cancelled marker) consistently
   * live and after reload. Distinct from `runOutcomes`, which is keyed by the
   * client-minted run id and only matches turnId after a reload.
   */
  turnOutcomes: Record<string, RunOutcome>;
  createdAt: number;
  /** Last server-confirmed metadata update, such as rename/title changes. */
  updatedAt: number;
  /** Last user or agent message activity in the conversation. */
  lastActivityAt: number;
  /** Projects referenced by the thread's prompts; used for chip display. */
  projects: Array<{entityName: string; projectName: string}>;
  /**
   * ownerUsername is the W&B username of the thread's owner, as the server
   * reports it. Null for a chat this browser started, which is the viewer's.
   * Only the owner writes to a thread; anyone else it's shared with reads it.
   */
  ownerUsername: string | null;
  /**
   * pendingQuestions holds structured questions the last completed turn asked.
   * When set, the composer presents them as a guided dialog for the next
   * prompt. Cleared when a new run starts.
   */
  pendingQuestions: AgentQuestion[] | null;
  /**
   * pendingNetworkAccessRequest holds the elevated-network-access request the
   * last completed turn made. When set, the composer presents a grant/deny
   * element. Cleared when a new run starts.
   */
  pendingNetworkAccessRequest: NetworkAccessRequest | null;
  /**
   * activeNetworkDomains is the network allowlist the most recent turn ran with
   * (its resolved `permissions.allowed_network_domains`). Derived from turn
   * data, not accumulated client-side: the composer offers these as removable
   * chips, and grants/revokes send a new allowlist on the next turn's patch.
   */
  activeNetworkDomains: string[];
}

// MAX_THREAD_TITLE_LENGTH defines the maximum supported title length, per the
// wb_agent contract: VARCHAR(255) + Pydantic max_length=255, counted in code
// points so emoji/non-Latin titles aren't over-limited.
export const MAX_THREAD_TITLE_LENGTH = 255;

/**
 * clampToTitleLength truncates to MAX_THREAD_TITLE_LENGTH counted in code
 * points (not UTF-16 units), so emoji and non-Latin titles aren't cut earlier
 * than the server allows. Safe to call on every keystroke.
 */
export function clampToTitleLength(s: string): string {
  const points = [...s];
  return points.length > MAX_THREAD_TITLE_LENGTH
    ? points.slice(0, MAX_THREAD_TITLE_LENGTH).join('')
    : s;
}

/**
 * sanitizeThreadTitle normalizes a user-entered title to the single-line form
 * the server stores. Preserves emoji ZWJ sequences, combining marks, bidi
 * controls, and non-Latin scripts; removes only control characters that render
 * as broken glyphs, then clamps to the server's length in code points.
 */
export function sanitizeThreadTitle(raw: string): string {
  const cleaned = raw
    .normalize('NFC')
    // Drop control chars, but fold whitespace controls (tab, newline) into a
    // space so words keep a gap; the collapse below then dedupes runs.
    .replace(/\p{Cc}/gu, c => (/\s/.test(c) ? ' ' : ''))
    .replace(/\s+/g, ' ')
    .trim();

  return clampToTitleLength(cleaned);
}

export interface MessageTree {
  /** activeChildId maps parent IDs to their active child ID. */
  activeChildId: Record<string, string>;
  /** children maps parent IDs to their ordered child IDs. */
  children: Record<string, string[]>;
  /** currentNodeId is the latest in the active branch. */
  currentNodeId: string | null;
  /** nodes maps IDs to the message nodes. */
  nodes: Record<string, MessageNode>;
  /** parentId maps node IDs to their parent IDs (null for root-level nodes). */
  parentId: Record<string, string | null>;
  /** rootChildren lists all top-level node IDs. */
  rootChildren: string[];
}

export interface MessageNode {
  id: string;
  role: MessageRole;
  content: ContentPart[];
  createdAt: number;
}

export function createDefaultState(): AgentChatState {
  return {
    threads: {},
    drafts: {},
    promotionAliases: {},
    activeThreadId: null,
    threadLoads: {},
    pendingRenames: {},
    pendingDeletes: new Set(),
    deletedThreadIds: new Set(),
  };
}

export function createEmptyThread(id: string, now = Date.now()): Thread {
  return {
    id,
    title: null,
    isPinned: false,
    status: 'idle',
    error: null,
    latestTurnId: null,
    messageTree: createEmptyTree(),
    currentRunId: null,
    currentMessageId: null,
    cancelRequested: false,
    runOutcomes: {},
    turnOutcomes: {},
    createdAt: now,
    updatedAt: now,
    lastActivityAt: now,
    loadStatus: 'loaded',
    projects: [],
    ownerUsername: null,
    pendingQuestions: null,
    pendingNetworkAccessRequest: null,
    activeNetworkDomains: [],
  };
}

// ThreadSummary is backend-derived metadata from GET /threads.
export type ThreadSummary = Pick<
  Thread,
  | 'id'
  | 'title'
  | 'isPinned'
  | 'updatedAt'
  | 'lastActivityAt'
  | 'createdAt'
  | 'projects'
  | 'ownerUsername'
>;

export function createThreadFromSummary(args: ThreadSummary): Thread {
  return {
    id: args.id,
    title: args.title,
    isPinned: args.isPinned,
    status: 'idle',
    error: null,
    latestTurnId: null,
    messageTree: createEmptyTree(),
    currentRunId: null,
    currentMessageId: null,
    cancelRequested: false,
    runOutcomes: {},
    turnOutcomes: {},
    createdAt: args.createdAt,
    updatedAt: args.updatedAt,
    lastActivityAt: args.lastActivityAt,
    loadStatus: 'summary',
    projects: args.projects,
    ownerUsername: args.ownerUsername,
    pendingQuestions: null,
    pendingNetworkAccessRequest: null,
    activeNetworkDomains: [],
  };
}

export function createEmptyTree(): MessageTree {
  return {
    nodes: {},
    parentId: {},
    children: {},
    rootChildren: [],
    currentNodeId: null,
    activeChildId: {},
  };
}

/**
 * isMeaningfulPart reports whether a part is worth sending in a user prompt.
 * Text must be non-blank; any non-text part (mention, image, etc.) qualifies on
 * its own.
 */
export function isMeaningfulPart(part: ContentPart): boolean {
  return part.type !== 'text' || part.text.trim().length > 0;
}

/**
 * hasMeaningfulContent reports whether `parts` contains anything worth sending
 * as a user prompt, per {@link isMeaningfulPart}.
 */
export function hasMeaningfulContent(parts: readonly ContentPart[]): boolean {
  return parts.some(isMeaningfulPart);
}

export type {
  ContentPart,
  ImagePart,
  PagePromptContent,
  ReadonlyJSONValue,
  ReportPageContent,
  ReportRunSet,
  RunDisplayNameStatus,
  RunSelectorGroupingKey,
  SweepMetric,
  SweepPageContent,
  TextPart,
  ThinkingPart,
  ToolCallPart,
  UnknownPart,
  WandbArtifactCollectionPart,
  WandbCallPart,
  WandbConversationPart,
  WandbEntityPart,
  WandbProjectPart,
  WandbRunPart,
  WandbWebPageStatePart,
  WandbWorkspacePanelPart,
  WorkspacePageContent,
} from '@wandb/forge-shell-contract/aria';
